import { Link } from "@tanstack/react-router";
import { ArrowRight, Calendar, Phone, Star, CheckCircle2, Sparkles } from "lucide-react";

const PHONE = "+1-719-419-3935";
const PHONE_DISPLAY = "(719) 419-3935";

type SeeOurWork = {
  /** Link target — either "/reviews" or any other internal route */
  to: "/reviews" | "/learn" | "/industries" | "/service-areas";
  /** Anchor text contextualized to the page (e.g. "See HVAC results in Colorado Springs") */
  label: string;
};

/**
 * Standardized bottom-of-page CTA used on every industry page and every
 * city × service page. Two consistent actions:
 *   1. Free 30-minute strategy call (primary)
 *   2. "See our work" — a context-aware proof link (currently → /reviews)
 *
 * The eyebrow + headline + supporting line accept overrides so each page
 * can tailor the framing while keeping the structure identical.
 */
export function PageCTA({
  eyebrow = "Free 30-minute strategy call",
  headline,
  description,
  seeOurWork,
}: {
  eyebrow?: string;
  /** Main headline — usually mentions the city, industry, or service. */
  headline: string;
  /** Supporting paragraph under the headline. */
  description: string;
  /** Required "see our work" link tailored to the page context. */
  seeOurWork: SeeOurWork;
}) {
  return (
    <section className="relative overflow-hidden bg-gradient-dark py-24 text-brand-cream sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-60" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-gold/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-gold/10 blur-3xl" />

      <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
        <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
          <Calendar className="h-3.5 w-3.5" /> {eyebrow}
        </span>
        <h2 className="mt-6 text-3xl font-black leading-[1.1] text-white sm:text-4xl lg:text-5xl">
          {headline}
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-brand-cream/85 sm:text-lg">
          {description}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/90">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" />
            No long-term contracts
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" />
            Real Colorado team
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" />
            Transparent pricing
          </span>
        </div>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-8 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            <Sparkles className="h-4 w-4" />
            Book My Free Strategy Call
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" />
            {PHONE_DISPLAY}
          </a>
        </div>

        <div className="mt-7">
          <Link
            to={seeOurWork.to}
            className="group inline-flex items-center gap-2 text-sm font-bold text-brand-gold hover:text-white"
          >
            <Star className="h-4 w-4 fill-current" />
            {seeOurWork.label}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
